/**
 * 규칙 작성 가이드 — 설계 가이드의 규칙을 **어떻게 쓰는지**(2026-10-07 사용자 요구).
 *
 * 사용자별이 아닌 **공용 문서 하나**다(서버 `designGuide/writingGuide.js`). 모두 읽고, 관리자만 고친다 — 고치기 단추는 서버가
 * `canEdit` 로 알려 줄 때만 보인다(권한 판정은 서버가 한다. 화면은 숨길 뿐이다). 파일 변환(AI)도 이 문서대로 규칙을 옮긴다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-toastify';
import { FiEdit3, FiSave, FiLoader, FiRotateCcw } from 'react-icons/fi';
import { getWritingGuide, saveWritingGuide } from '../../services/assistantClient';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';

function WritingGuidePanel({ t }) {
    const [data, setData] = useState(null);       // { guide, canEdit }
    const [error, setError] = useState(null);
    const [draft, setDraft] = useState(null);     // null = 보기, 문자열 = 고치는 중
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        getWritingGuide()
            .then((r) => { if (alive) setData(r); })
            .catch((err) => { if (alive) setError(formatApiError(err, t)); });
        return () => { alive = false; };
    }, [t]);

    const save = useCallback(async (body) => {
        setBusy(true); setError(null);
        try {
            const r = await saveWritingGuide(body);
            setData(d => ({ ...d, guide: r.guide }));
            setDraft(null);
            toast.success(t('assistant:designGuide.writing.saved'));
        } catch (err) {
            setError(formatApiError(err, t));
        } finally { setBusy(false); }
    }, [t]);

    if (error && !data) return <div className="gm-dropped">{error}</div>;
    if (!data) return <div className="dg-writing"><FiLoader size={12} className="tcf-spin" /></div>;
    const g = data.guide;
    return (
        <div className="dg-writing">
            <div className="tcf-row">
                <span className="ta-meta">
                    {g.isDefault ? t('assistant:designGuide.writing.default')
                        : t('assistant:designGuide.writing.meta', { at: storedTime(g.updatedAt), by: g.updatedBy })}
                    {' · '}{t('assistant:designGuide.writing.shared')}
                </span>
                {data.canEdit && draft === null ? (
                    <button type="button" className="tcf-btn" onClick={() => setDraft(g.body)}>
                        <FiEdit3 size={11} /> {t('assistant:design.edit')}
                    </button>
                ) : null}
            </div>
            {draft === null ? (
                <div className="dg-writingBody"><ReactMarkdown remarkPlugins={[remarkGfm]}>{g.body}</ReactMarkdown></div>
            ) : (
                <>
                    <textarea className="tcf-input dg-writingEdit" rows={16} value={draft} onChange={(e) => setDraft(e.target.value)} />
                    {error ? <div className="gm-dropped">{error}</div> : null}
                    <div className="tcf-actions">
                        <button type="button" className="tcf-btn" disabled={busy} onClick={() => save('')}
                            title={t('assistant:designGuide.writing.resetTip')}>
                            <FiRotateCcw size={11} /> {t('assistant:designGuide.writing.reset')}
                        </button>
                        <button type="button" className="tcf-btn" disabled={busy} onClick={() => { setDraft(null); setError(null); }}>
                            {t('common:ui.cancel')}
                        </button>
                        <button type="button" className="tcf-btn tcf-primary" disabled={busy || !draft.trim()} onClick={() => save(draft)}>
                            {busy ? <FiLoader size={11} className="tcf-spin" /> : <FiSave size={11} />} {t('common:action.save')}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
WritingGuidePanel.propTypes = { t: PropTypes.func.isRequired };

export default WritingGuidePanel;
